import { useCallback, useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { CampoDeTexto, CampoDeTextoComMascara } from "../../componentes";
import Container from "../../componentes/Container";
import { BotaoComTextoEIcone, Dialogo } from "@digix-ui/componentes";
import "./styles.scss";
import { removerAcentos } from "./utils/removerAcentos";
import { formatarDataParaInput } from "./utils/formatarDataParaInput";
import { buscarCidades, Cidade } from "./services/buscarCidades";
import { useMutation, useQuery } from "@tanstack/react-query";
import { bloquearCidade } from "./services/bloquearCidade";
import { desbloquearCidade } from "./services/desbloquearCidade";
import { queryClient } from "../../utils/query";
import { atualizarPermissaoCidade } from "./services/atualizarPermissaoCidade";
import { converterParaIso } from "./utils/converterParaISO";
import { ehDataValida } from "./utils/ehDataValida";
import { dataEhPosterior } from "./utils/dataEhPosterior";

const ListagemDeMunicipios = () => {
  const [filtroDigitado, setFiltroDigitado] = useState("");
  const [statusSelecionado, setStatusSelecionado] = useState("TODOS");
  const [modalEditarAberto, setModalEditarAberto] = useState(false);
  const [modalBloquearAberto, setModalBloquearAberto] = useState(false);
  const [menuAbertoIndex, setMenuAbertoIndex] = useState<number | null>(null);
  const [cidadeSelecionada, setCidadeSelecionada] = useState<Cidade | null>(
    null
  );

  const [cidadeParaBloquearOuDesbloquear, setCidadeParaBloquearOuDesbloquear] =
    useState<Cidade | null>(null);

  const isBloqueada =
    !cidadeParaBloquearOuDesbloquear?.status ||
    cidadeParaBloquearOuDesbloquear.status.toUpperCase() === "FECHADA";
  const colunas = ["Nome", "Abertura", "Status", "Ações"];
  const [semDataFechamento, setSemDataFechamento] = useState(false);

  const { setValue, watch, reset, control } = useForm({
    mode: "onChange",
    defaultValues: {
      dataAbertura: "",
      dataFechamento: "",
    },
  });

  const {
    data: cidades = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ["cidades"],
    queryFn: buscarCidades,
  });

  const { mutate: bloquear, isPending: bloqueando } = useMutation({
    mutationFn: (id: number) => bloquearCidade(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["cidades"] }),
  });

  const { mutate: desbloquear, isPending: desbloqueando } = useMutation({
    mutationFn: (id: number) => desbloquearCidade(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["cidades"] }),
  });

  const { mutate: atualizarCidade } = useMutation({
    mutationFn: ({
      id,
      dataAbertura,
      dataFechamento,
    }: {
      id: number;
      dataAbertura: string;
      dataFechamento: string | null;
    }) => atualizarPermissaoCidade(id, { dataAbertura, dataFechamento }),
    onSuccess: () => {
      setModalEditarAberto(false);
      queryClient.invalidateQueries({ queryKey: ["cidades"] });
    },
  });

  const dataAbertura = watch("dataAbertura");
  const dataFechamento = watch("dataFechamento");

  const menuRef = useRef<HTMLDivElement | null>(null);

  const cidadesFiltradas = cidades.filter((cidade) => {
    const nome = removerAcentos(cidade.cidade);
    const filtro = removerAcentos(filtroDigitado)?.toUpperCase() || "";
    const statusNormalizado = cidade.status?.toUpperCase() || "";
    const statusFiltro = statusSelecionado.toUpperCase();

    const statusOk =
      statusSelecionado === "TODOS" ||
      (statusFiltro === "NÃO PARCEIRA" && !cidade.status) ||
      statusNormalizado === statusFiltro;

    return nome.includes(filtro) && statusOk;
  });

  const abrirMenu = (index: number, e?: React.MouseEvent) => {
    e?.stopPropagation();
    setMenuAbertoIndex((atual) => (atual === index ? null : index));
  };

  useEffect(() => {
    const fecharMenu = () => setMenuAbertoIndex(null);
    window.addEventListener("click", fecharMenu);
    return () => window.removeEventListener("click", fecharMenu);
  }, []);

  const abrirModalEditar = (cidade: Cidade) => {
    setCidadeSelecionada(cidade);
    setSemDataFechamento(!cidade.dataFechamento);
    reset({
      dataAbertura: formatarDataParaInput(cidade.dataAbertura),
      dataFechamento: formatarDataParaInput(cidade.dataFechamento),
    });
    setModalEditarAberto(true);
    setMenuAbertoIndex(null);
  };

  const corpoDoModalEditar = useCallback(
    () => (
      <div className="grade__linha grade__coluna u-largura-total">
        <Controller
          name="dataAbertura"
          control={control}
          render={({ field }) => (
            <CampoDeTextoComMascara
              name="dataAbertura"
              label="Data para abertura"
              mascara="99/99/9999"
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              colunas="12"
            />
          )}
        />
        <Controller
          name="dataFechamento"
          control={control}
          render={({ field }) => (
            <CampoDeTextoComMascara
              name="dataFechamento"
              label="Data para fechamento"
              mascara="99/99/9999"
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              colunas="12"
              disabled={semDataFechamento}
            />
          )}
        />
        <div className="grade__coluna u-margem-top-pequena">
          <label className="formulario__checkbox-label u-display-flex u-items-center">
            <input
              type="checkbox"
              checked={semDataFechamento}
              onChange={(e) => {
                setSemDataFechamento(e.target.checked);
                if (e.target.checked) setValue("dataFechamento", "");
              }}
              style={{ marginRight: "8px" }}
            />
            <span className="u-texto-pequeno">
              Sem data de fechamento definida
            </span>
          </label>
        </div>
      </div>
    ),
    [control, semDataFechamento, setValue]
  );

  const rodapeDoModalEditar = useCallback(() => {
    const dataAberturaInvalida = !ehDataValida(dataAbertura);
    const dataFechamentoObrigatoriaInvalida =
      !semDataFechamento &&
      (!ehDataValida(dataFechamento) ||
        !dataEhPosterior(dataAbertura, dataFechamento));

    const salvarDesabilitado =
      dataAberturaInvalida || dataFechamentoObrigatoriaInvalida;

    let mensagemErro = "";
    if (dataAberturaInvalida) {
      mensagemErro = "Preencha uma data de abertura válida (dd/mm/aaaa).";
    } else if (dataFechamentoObrigatoriaInvalida) {
      mensagemErro =
        "Data de fechamento deve ser válida e posterior à data de abertura.";
    }

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
        {mensagemErro && (
          <div
            style={{
              color: "#c53030",
              fontSize: "0.75rem", // fonte pequena
              marginBottom: "0.25rem",
            }}
          >
            {mensagemErro}
          </div>
        )}

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: "1rem", // espaçamento entre botões
          }}
        >
          <button
            className="botao botao_contorno botao_cor-secundaria"
            onClick={() => setModalEditarAberto(false)}
          >
            Cancelar
          </button>

          <button
            className="botao botao_cor-secundaria"
            onClick={() => {
              if (!cidadeSelecionada || salvarDesabilitado) return;

              const abertura = converterParaIso(dataAbertura);
              const fechamento = semDataFechamento
                ? null
                : converterParaIso(dataFechamento);

              atualizarCidade({
                id: cidadeSelecionada.id,
                dataAbertura: abertura,
                dataFechamento: fechamento,
              });
            }}
            disabled={salvarDesabilitado}
          >
            Salvar
          </button>
        </div>
      </div>
    );
  }, [
    dataAbertura,
    dataFechamento,
    semDataFechamento,
    atualizarCidade,
    cidadeSelecionada,
  ]);

  return (
    <>
      <Container comFundo tamanhoDoContainer="grande" comCabecalhoFixo>
        <div className="formulario">
          <h2 className="cartao__titulo">Municípios</h2>
          <div className="grade__linha filtros-municipios u-margem-superior-media">
            <CampoDeTexto
              name="cidade"
              value={filtroDigitado}
              onChange={(e) => setFiltroDigitado(e.target.value)}
              onBlur={() => {}}
              placeholder="Busque por cidade"
              tipo="text"
              label="Busque uma cidade"
              colunas="4"
            />
            <div className="grade__coluna grade__coluna_4">
              <label className="formulario__label">Filtrar por status</label>
              <select
                className="formulario__input"
                value={statusSelecionado}
                onChange={(e) => setStatusSelecionado(e.target.value)}
              >
                {["TODOS", "ABERTA", "FECHADA", "NÃO PARCEIRA"].map(
                  (status) => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  )
                )}
              </select>
            </div>
          </div>

          <table className="tabela tabela_com-largura-total tabela_listrada tabela_com-borda">
            <thead>
              <tr>
                {colunas.map((item) => (
                  <th
                    key={item}
                    className={`is-cor-cinza-20 u-texto-alinhado-ao-${
                      item === "Nome" ? "esquerda" : "centro"
                    }`}
                  >
                    {item}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                [...Array(5)].map((_, i) => (
                  <tr key={i}>
                    {colunas.map((_, j) => (
                      <td key={j}>
                        <div className="skeleton skeleton-linha" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : error ? (
                <tr>
                  <td
                    colSpan={colunas.length}
                    className="u-texto-alinhado-ao-centro u-cor-vermelha"
                  >
                    Ocorreu um erro ao carregar os municípios.
                  </td>
                </tr>
              ) : cidadesFiltradas.length === 0 ? (
                <tr>
                  <td
                    colSpan={colunas.length}
                    className="u-texto-alinhado-ao-centro"
                  >
                    Nenhum município encontrado.
                  </td>
                </tr>
              ) : (
                cidadesFiltradas.map((cidade, index) => (
                  <tr key={cidade.id}>
                    <td>{cidade.cidade}</td>
                    <td className="u-texto-alinhado-ao-centro">
                      {cidade.descricaoDoPeriodo}
                    </td>
                    <td className="u-texto-alinhado-ao-centro">
                      {cidade.status ? (
                        <span
                          className={`status ${
                            cidade.status.toUpperCase() === "ABERTA"
                              ? "status--aberto"
                              : "status--fechado"
                          }`}
                        >
                          {cidade.status.toUpperCase()}
                        </span>
                      ) : (
                        <span className="status status--nao-parceira">
                          NÃO PARCEIRA
                        </span>
                      )}
                    </td>
                    <td className="u-texto-alinhado-ao-centro">
                      <div className="acoes" ref={menuRef}>
                        <BotaoComTextoEIcone
                          tamanho="pequeno"
                          cor="terciaria"
                          tipo="button"
                          posicaoDoIcone="direita"
                          visual="sem-preenchimento"
                          icone="far fa-chevron-down"
                          onClick={() => {
                            const e = window.event as MouseEvent;
                            e?.stopPropagation();
                            abrirMenu(index);
                          }}
                        >
                          Ações
                        </BotaoComTextoEIcone>
                        {menuAbertoIndex === index && (
                          <ul className="inscricao-acoes__lista">
                            {cidade.status ? (
                              <li className="inscricao-acoes__item">
                                <button
                                  onClick={() => abrirModalEditar(cidade)}
                                  className="inscricao-acoes__botao"
                                >
                                  <i className="inscricao-acoes__icone far fa-edit"></i>{" "}
                                  Editar
                                </button>
                              </li>
                            ) : (
                              <li className="inscricao-acoes__item">
                                <button
                                  className="inscricao-acoes__botao"
                                  disabled
                                >
                                  <i className="inscricao-acoes__icone far fa-edit"></i>{" "}
                                  Editar
                                </button>
                              </li>
                            )}

                            <li className="inscricao-acoes__item">
                              <button
                                onClick={() => {
                                  if (!cidade.status) {
                                    abrirModalEditar(cidade);
                                  } else {
                                    setCidadeParaBloquearOuDesbloquear(cidade);
                                    setModalBloquearAberto(true);
                                  }
                                }}
                                className="inscricao-acoes__botao"
                              >
                                <i className="inscricao-acoes__icone far fa-lock"></i>
                                {!cidade.status ||
                                cidade.status.toUpperCase() === "FECHADA"
                                  ? "Desbloquear"
                                  : "Bloquear"}
                              </button>
                            </li>
                          </ul>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          <Dialogo
            tamanho="mini"
            deveSerExibido={modalEditarAberto}
            fecharDialogo={() => setModalEditarAberto(false)}
            titulo="Editar cidade"
            corpo={corpoDoModalEditar}
            rodape={rodapeDoModalEditar}
          />

          <Dialogo
            tamanho="mini"
            deveSerExibido={modalBloquearAberto}
            fecharDialogo={() => setModalBloquearAberto(false)}
            titulo={
              isBloqueada ? "Confirmar desbloqueio" : "Confirmar bloqueio"
            }
            corpo={() => (
              <p>
                Tem certeza que deseja{" "}
                {isBloqueada ? "desbloquear" : "bloquear"} a cidade{" "}
                <strong>{cidadeParaBloquearOuDesbloquear?.cidade}</strong>?
              </p>
            )}
            rodape={() => (
              <>
                <button
                  className="botao botao_cor-secundaria"
                  onClick={() => {
                    if (!cidadeParaBloquearOuDesbloquear) return;
                    const isBloqueada =
                      !cidadeParaBloquearOuDesbloquear.status ||
                      cidadeParaBloquearOuDesbloquear.status.toUpperCase() ===
                        "FECHADA";
                    isBloqueada
                      ? desbloquear(cidadeParaBloquearOuDesbloquear.id)
                      : bloquear(cidadeParaBloquearOuDesbloquear.id);
                    setModalBloquearAberto(false);
                  }}
                  disabled={bloqueando || desbloqueando}
                >
                  Sim, confirmar
                </button>
                <button
                  className="botao botao_contorno botao_cor-secundaria"
                  onClick={() => setModalBloquearAberto(false)}
                >
                  Cancelar
                </button>
              </>
            )}
          />
        </div>
      </Container>
    </>
  );
};

export default ListagemDeMunicipios;
